// src/components/StripePaymentForm.tsx
import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import api from '../services/api.ts';

declare const process: {
    env: {
        REACT_APP_STRIPE_PUBLISHABLE_KEY: string;
    };
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...');


interface BookingData {
    trainer_id: string;
    scheduled_date: string;
    scheduled_time: string;
    duration_minutes: number;
    customer_address: string;
    notes: string;
}

interface PaymentFormProps {
    bookingData: BookingData;
    amount: number;
    onSuccess: () => void;
    onCancel: () => void;
}

function CheckoutForm({ bookingData, amount, onSuccess, onCancel }: PaymentFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            // Confirm the payment
            const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: 'if_required',
                confirmParams: {
                    return_url: window.location.origin + '/booking-confirmation',
                }
            });

            if (stripeError) {
                setError(stripeError.message || 'Payment failed');
                setProcessing(false);
                return;
            }

            if (paymentIntent?.status === 'succeeded') {
                // Create the booking with payment confirmation
                
                const response = await api.post('/bookings/confirm-with-payment/', {
                    ...bookingData,
                    payment_intent_id: paymentIntent.id
                });

                if (response.status === 200) {
                    onSuccess();
                } else {
                    setError(response.data.error || 'Booking creation failed');
                }
            }
        } catch (err: any) {
            console.error('Payment error:', err);
            setError(err.response?.data?.error || 'An unexpected error occurred');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <PaymentElement />

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <p className="font-medium">Payment Error</p>
                    <p className="text-sm mt-1">{error}</p>
                </div>
            )}

            <div className="flex space-x-3 pt-4">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={processing}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!stripe || processing}
                    className="flex-1 bg-orange-400 text-white py-3 rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                    {processing ? (
                        <span className="flex items-center justify-center">
                            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                        </span>
                    ) : (
                        `Pay $${amount.toFixed(2)}`
                    )}
                </button>
            </div>
        </form>
    );
}

export function StripePaymentForm(props: PaymentFormProps) {
    const [clientSecret, setClientSecret] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const createPaymentIntent = async () => {
            try {
                const response = await api.post('/payments/create-intent/', {
                    trainer_id: props.bookingData.trainer_id,
                    duration_minutes: props.bookingData.duration_minutes
                });

                if (response.data.clientSecret) {
                    setClientSecret(response.data.clientSecret);
                } else {
                    setError('Failed to initialize payment');
                }
            } catch (err: any) {
                console.error('Error creating payment intent:', err);
                setError(err.response?.data?.error || 'Failed to initialize payment. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        createPaymentIntent();
    }, [props.bookingData.trainer_id, props.bookingData.duration_minutes]);

    if (loading) {
        return (
            <div className="py-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading payment form...</p>
            </div>
        );
    }

    if (error || !clientSecret) {
        return (
            <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <p className="font-medium">Unable to Initialize Payment</p>
                    <p className="text-sm mt-1">{error || 'Please try again later.'}</p>
                </div>
                <button
                    onClick={props.onCancel}
                    className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm {...props} />
        </Elements>
    );
}